/**
 * Master CSV import template — the single source of truth for what
 * headers the Lead + Client forms and the CSV importer agree on.
 *
 * A "Download CSV Template" button on both Import pages triggers a Blob
 * download built from this file so the file lives outside `/public`
 * (bundlers pull it into the build and it doesn't stale-cache) and the
 * headers, order, and sample row always match what the parser accepts.
 *
 * DO NOT add brokerage fields here. Broker identity is the AGENT'S
 * (Settings → Brokerage & licensing) — not a per-lead / per-client
 * attribute — and mixing it into a contact CSV would confuse agents
 * and open a compliance foot-gun.
 */

// Column order matters — this is the order the header row appears in
// the downloaded file. Kept broad-then-specific so a scanning agent sees
// identity first, then details.
export const CSV_TEMPLATE_HEADERS = [
  "status",             // "Lead" or "Client" — routes the row on import
  "first_name",
  "last_name",
  "email",
  "phone",
  "address",
  "source",             // Zillow, Referral, Open House, …
  "type",               // buyer / seller / both
  "property_interest",  // free-text — "Luxury single-family in Arcadia"
  "price_min",
  "price_max",
  "notes",
];

// A single realistic sample row — real names avoided so agents don't
// accidentally import "Emma Sterling" into production.
export const CSV_TEMPLATE_SAMPLE_ROW = [
  "Client",
  "Sample",
  "Contact",
  "sample.contact@example.com",
  "480-555-0100",
  "7373 E Doubletree Ranch Rd, Scottsdale, AZ 85258",
  "Referral",
  "buyer",
  "Luxury single-family in Arcadia; pool required",
  "950000",
  "1250000",
  "Interested in a fall closing; pre-approved with Chase.",
];

// Escape a single cell per RFC 4180 — quote when the value contains a
// comma, newline, or the quote itself; double any embedded quotes.
function escapeCsvCell(cell) {
  const s = cell == null ? "" : String(cell);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Serialize headers + sample row into an RFC 4180 CSV body string. */
export function buildCsvTemplate() {
  const lines = [
    CSV_TEMPLATE_HEADERS.map(escapeCsvCell).join(","),
    CSV_TEMPLATE_SAMPLE_ROW.map(escapeCsvCell).join(","),
  ];
  return lines.join("\r\n") + "\r\n";
}

/**
 * Trigger a browser download of the master CSV template.
 *
 * `fileName` defaults to a mode-aware label so agents importing on the
 * Clients tab get `ikey-clients-import-template.csv` and Leads tab gets
 * `ikey-leads-import-template.csv` — both files are identical, only the
 * name differs. Works on iOS Safari + Chrome/Android via the anchor
 * + `URL.createObjectURL` approach; no server round-trip required.
 */
export function downloadCsvTemplate(fileName = "ikey-import-template.csv") {
  const body = buildCsvTemplate();
  // Prepend the UTF-8 BOM so Excel on Windows opens the file with the
  // right encoding — otherwise agents see "Ã©" instead of accented
  // characters in their sample notes.
  const blob = new Blob(["\ufeff" + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  // Mobile Safari won't fire the download unless the anchor is in the DOM
  // when clicked — append + click + remove is the reliable pattern.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Chrome revokes automatically on click, but revoke defensively so
  // long-lived pages don't leak object URLs after repeated downloads.
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}
